// The number on the app icon — the one signal that reaches an operator who is
// not looking at the app at all.
//
// WHY THIS EXISTS SEPARATELY FROM THE BELL. The bell is inside the app, so it
// only answers a question somebody already opened the app to ask. An operator
// monitoring the day from a phone in their pocket needs to know something
// happened WITHOUT opening anything, and on an installed web app the icon badge
// is the only surface besides a push notification that can say so.
//
// PLATFORM REALITY, stated because it decides what this can promise:
//   iOS/iPadOS 16.4+  — works, and is the reason this exists. Home Screen web
//                       apps only; the number is drawn once notification
//                       permission has been granted (setAppBadge may be called
//                       before that, and is harmless).
//   Desktop Chrome/Edge/Safari — works.
//   Chrome for Android, Samsung Internet, Firefox — NOT SUPPORTED at any
//                       version. Android instead marks the launcher icon with
//                       its own notification dot whenever a real notification
//                       sits in the tray, which is driven by push and by nothing
//                       we can call. So this module is a deliberate no-op there,
//                       and the Android answer to "something changed" is push.
//
// Everything here therefore degrades to silence rather than to an error: on the
// majority platform the API is simply absent.

/** The slice of `navigator` this needs, so a test can supply one. */
export interface BadgeTarget {
  setAppBadge?: (count?: number) => Promise<void>
  clearAppBadge?: () => Promise<void>
}

/**
 * What to do with a given unread count — decided separately from doing it, so
 * the rules are testable without a browser that implements the API.
 *
 * `clear` rather than `set 0` for an empty inbox: passing 0 to setAppBadge is
 * specified to show a badge with NO number ("something is there") on some
 * platforms, which is precisely the wrong answer for "nothing is waiting".
 */
export function badgeAction(count: number): { kind: 'set'; count: number } | { kind: 'clear' } {
  if (!Number.isFinite(count) || count <= 0) return { kind: 'clear' }
  // Whole notifications only. A fractional count is not something the feed can
  // produce, but the API throws on one rather than rounding.
  return { kind: 'set', count: Math.floor(count) }
}

/**
 * Apply an unread count to the app icon. Never throws and never rejects.
 *
 * Failure is EXPECTED here, not exceptional: the API is missing on Android
 * Chrome, and on iOS it can reject while the web app is not installed. A badge
 * that could break its caller would be a bad trade, since every caller is on a
 * path that matters more than this one (the notification poll, a dismissal,
 * sign-out).
 */
export async function applyAppBadge(count: number, target?: BadgeTarget | null): Promise<void> {
  const nav = target ?? (typeof navigator !== 'undefined' ? (navigator as BadgeTarget) : null)
  if (!nav) return
  const action = badgeAction(count)
  try {
    if (action.kind === 'clear') await nav.clearAppBadge?.()
    else await nav.setAppBadge?.(action.count)
  } catch {
    // Unsupported, not installed, or permission not granted. All three mean the
    // same thing to us — there is no icon to write on — and none is worth
    // telling anybody about.
  }
}
